// src/components/EquipmentManager/AssignEquipmentModal.js

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styles from './AssignEquipmentModal.module.css';
import { FaTimes, FaUsers, FaBox, FaCalendarAlt, FaHashtag, FaCheckCircle, FaStickyNote, FaStar } from 'react-icons/fa';
import axios from 'axios';

const AssignEquipmentModal = ({
  isOpen,
  onClose,
  programId,
  assignmentToEdit,
  onAssignmentUpdated,
  preSelectedEquipment = null, // NEW: Pre-selected equipment from card
}) => {
  // State variables
  const [players, setPlayers] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [condition, setCondition] = useState('good');
  const [error, setError] = useState(null);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [loadingEquipments, setLoadingEquipments] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sportName, setSportName] = useState(''); // New state variable

  // Reset form state
  const resetForm = () => {
    setSelectedPlayer('');
    if (!preSelectedEquipment) {
      setSelectedEquipment('');
    }
    setNotes('');
    setDueDate('');
    setQuantity(1);
    setCondition('good');
    setError(null);
    setSuccess(false);
    setAssigning(false);
  };
  
  // Pre-populate equipment if provided
  React.useEffect(() => {
    if (preSelectedEquipment && preSelectedEquipment._id) {
      setSelectedEquipment(preSelectedEquipment._id);
      console.log('Pre-selected equipment:', preSelectedEquipment.equipmentName);
    }
  }, [preSelectedEquipment]);

  // Fetch clubId from program details
  const [clubId, setClubId] = useState(null);

  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch program details');
        }
        const data = await response.json();
        
        // Extract clubId correctly (handle both string and object cases)
        const extractedClubId =
          data.clubId && typeof data.clubId === 'object'
            ? data.clubId._id
            : data.clubId;
            
        console.log('Extracted clubId from program for equipment modal:', extractedClubId);
        setClubId(extractedClubId);
        setSportName(data.sport);
      } catch (error) {
        console.error('Error fetching program details:', error);
        setError(error.message);
      }
    };

    if (isOpen && programId) {
      fetchProgramDetails();
    }
  }, [isOpen, programId]);

  // Fetch eligible users for equipment assignment (EXACT COPY from TeamsPage.js and TeamItemModal.js)
  useEffect(() => {
    const fetchEligibleUsers = async () => {
      try {
        // Use new backend endpoint that handles player availability and PlayerProfile integration
        // For equipment assignment, we don't exclude any teams
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}/available-players`;
        
        console.log('Fetching eligible users for equipment assignment from:', url);
        const response = await fetch(url);
        console.log('Response status:', response.status, 'Response ok:', response.ok);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Raw response data for equipment assignment:', data);
          const availablePlayers = data.availablePlayers || [];
          
          if (availablePlayers.length === 0) {
            console.warn('No available players returned from new endpoint for equipment assignment, falling back to old method');
          } else {
            // Transform to match expected format
            const transformedUsers = availablePlayers.map(player => ({
              _id: player._id,
              firstName: player.firstName || '',
              lastName: player.lastName || '',
              name: player.fullName || player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
              fullName: player.fullName || player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
              displayName: player.displayName || `${player.fullName || player.name || 'Unknown'} (${player.roleContext || 'Player'})`,
              email: player.email,
              roles: player.roles || ['player'],
              programRole: player.roleContext || 'player',
              programStatus: player.programStatus || 'enrolled',
              playerProfile: player.playerProfile
            }));
            
            console.log('Available players for equipment assignment:', transformedUsers);
            console.log('Sample user structure for equipment assignment:', transformedUsers[0]);
            setPlayers(transformedUsers);
            setLoadingPlayers(false);
            return;
          }
        }
        
        // Fallback to old method if new endpoint fails or returns no data
        console.warn('New endpoint failed or returned no data for equipment assignment, falling back to old method');
        
        // Fetch program members and followers who are enrolled or have management roles
        // Try both capitalized and lowercase status parameters to handle backend inconsistencies
        const [programMembersResponse, programFollowersEnrolledResponse, programFollowersLowercaseResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/members`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/followers?status=Enrolled`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/followers?status=enrolled`)
        ]);

        let eligibleUsers = [];

        // Process program members (includes program management)
        if (programMembersResponse.ok) {
          const membersData = await programMembersResponse.json();
          const members = Array.isArray(membersData) ? membersData : [];
          
          console.log('AssignEquipmentModal - Raw program members data:', members);
          
          // Include enrolled members and program management roles (case-insensitive)
          const programEligible = members.filter(member => {
            const status = member.status?.toLowerCase();
            const role = member.role?.toLowerCase();
            
            console.log(`AssignEquipmentModal - Checking member: ${member.user?.name || member.name}, status: "${member.status}", role: "${member.role}"`);
            
            // Include enrolled members (case-insensitive)
            if (status === 'enrolled') return true;
            
            // Include program management roles regardless of status
            const managementRoles = ['programdirector', 'programadmin', 'headcoach', 'assistantcoach', 'teammanager', 'registrar', 'communicationslead', 'equipmentfacilitieslead'];
            if (managementRoles.includes(role)) return true;
            
            return false;
          });

          console.log('AssignEquipmentModal - Filtered program eligible users:', programEligible);
          eligibleUsers = [...eligibleUsers, ...programEligible];
        }

        // Process program followers (enrolled members) - try both response formats
        const processFollowers = (followersData, source) => {
          const followers = followersData.followers || [];
          console.log(`AssignEquipmentModal - Raw ${source} followers data:`, followers);
          
          // Add enrolled followers (avoid duplicates, case-insensitive)
          const enrolledFollowers = followers.filter(follower => {
            const status = follower.status?.toLowerCase();
            const isEnrolled = status === 'enrolled';
            const isDuplicate = eligibleUsers.some(existing => 
              existing.userId === follower.userId || existing.user?._id === follower.user?._id
            );
            
            console.log(`AssignEquipmentModal - Checking ${source} follower: ${follower.user?.name || follower.name}, status: "${follower.status}", enrolled: ${isEnrolled}, duplicate: ${isDuplicate}`);
            
            return isEnrolled && !isDuplicate;
          });

          console.log(`AssignEquipmentModal - Filtered ${source} enrolled followers:`, enrolledFollowers);
          return enrolledFollowers;
        };

        // Process both follower responses
        if (programFollowersEnrolledResponse.ok) {
          const followersData = await programFollowersEnrolledResponse.json();
          const enrolledFollowers = processFollowers(followersData, 'Enrolled');
          eligibleUsers = [...eligibleUsers, ...enrolledFollowers];
        }

        if (programFollowersLowercaseResponse.ok) {
          const followersData = await programFollowersLowercaseResponse.json();
          const enrolledFollowers = processFollowers(followersData, 'enrolled');
          eligibleUsers = [...eligibleUsers, ...enrolledFollowers];
        }

        // Fetch ALL club members (not just management)
        console.log('AssignEquipmentModal clubId for club members fetch:', clubId);
        if (clubId) {
          try {
            const clubMembersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clubs/${clubId}/members`);
            if (clubMembersResponse.ok) {
              const clubMembersData = await clubMembersResponse.json();
              const clubMembers = Array.isArray(clubMembersData) ? clubMembersData : (clubMembersData.members || []);
              
              console.log('AssignEquipmentModal - Raw club members data:', clubMembers);
              
              // Include ALL club members (not just management)
              const allClubMembers = clubMembers.map(member => {
                // Extract user name from various possible formats
                let userName = 'Unknown User';
                if (member.user?.name) {
                  userName = member.user.name;
                } else if (member.user?.firstName || member.user?.lastName) {
                  userName = [member.user.firstName, member.user.lastName].filter(Boolean).join(' ').trim();
                } else if (member.name) {
                  userName = member.name;
                } else if (member.user?.email) {
                  userName = member.user.email;
                } else if (member.email) {
                  userName = member.email;
                }
                
                return {
                  _id: member.user?._id || member.userId,
                  name: userName,
                  email: member.user?.email || member.email,
                  roles: ['club_member'], // Mark as club member
                  programRole: member.role?.toLowerCase() === 'creator' || member.role?.toLowerCase() === 'owner' || member.role?.toLowerCase() === 'manager' ? 'club_management' : 'club_member',
                  programStatus: 'club_member',
                  clubRole: member.role
                };
              }).filter(user => user._id);

              console.log('AssignEquipmentModal - Processed club members:', allClubMembers);

              // Add club members (avoid duplicates)
              allClubMembers.forEach(clubMember => {
                const isDuplicate = eligibleUsers.some(existing => 
                  existing.userId === clubMember._id || existing.user?._id === clubMember._id
                );
                
                console.log(`AssignEquipmentModal - Checking club member: ${clubMember.name}, duplicate: ${isDuplicate}`);
                
                if (!isDuplicate) {
                  eligibleUsers.push({
                    user: {
                      _id: clubMember._id,
                      name: clubMember.name,
                      email: clubMember.email
                    },
                    userId: clubMember._id,
                    role: clubMember.programRole,
                    status: 'club_member',
                    clubRole: clubMember.clubRole
                  });
                }
              });
            }
          } catch (error) {
            console.error('Error fetching club members for equipment assignment:', error);
          }
        }

        // Transform the data to match the expected user format (fallback method)
        const transformedUsers = eligibleUsers.map(member => {
          const user = member.user || {};
          
          // Better name extraction logic
          let fullName = 'Unknown User';
          if (user.name) {
            fullName = user.name;
          } else if (member.name) {
            fullName = member.name;
          } else if (user.firstName || user.lastName) {
            fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
          } else if (user.email) {
            fullName = user.email;
          } else if (member.email) {
            fullName = member.email;
          }
          
          return {
            _id: user._id || member.userId,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            name: fullName,
            fullName: fullName,
            displayName: `${fullName} (${member.role || 'Player'})`,
            email: user.email || member.email,
            roles: [member.role || 'player'], // Map program role to user roles array
            programRole: member.role,
            programStatus: member.status
          };
        }).filter(user => user._id); // Remove users without valid IDs

        console.log('Eligible users for equipment assignment (fallback):', transformedUsers);
        setPlayers(transformedUsers);
      } catch (error) {
        console.error('Error fetching eligible users for equipment assignment:', error);
        // Fallback to empty array
        setPlayers([]);
      } finally {
        setLoadingPlayers(false);
      }
    };

    if (isOpen && programId) {
      // Only reset form if we're NOT editing an assignment
      if (!assignmentToEdit) {
        console.log('AssignEquipmentModal - Resetting form for new assignment');
        resetForm();
      } else {
        console.log('AssignEquipmentModal - Skipping resetForm because we are editing an assignment');
      }
      fetchEligibleUsers();
    }
  }, [isOpen, programId, clubId, assignmentToEdit]);

  // Fetch equipments after obtaining sportName
  useEffect(() => {
    const fetchEquipments = async () => {
      if (!sportName) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/sports-equipments/sport/${encodeURIComponent(
            sportName
          )}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch equipments');
        }
        const data = await response.json();
        // Ensure data is an array
        const equipmentsArray = Array.isArray(data) ? data : (data.equipments || []);
        setEquipments(equipmentsArray);
      } catch (error) {
        console.error('Error fetching equipments:', error);
        setEquipments([]); // Set empty array on error
        // Don't set error message - equipment list can be empty
      } finally {
        setLoadingEquipments(false);
      }
    };

    if (isOpen && sportName) {
      fetchEquipments();
    }
  }, [isOpen, sportName]);

  // Helper function to format user display name with role context (IMPROVED)
  const formatUserDisplay = (user) => {
    // Use displayName if available (from backend PlayerProfile integration)
    if (user.displayName) {
      return user.displayName;
    }
    
    // Enhanced name extraction logic
    let name = 'Unknown User';
    if (user.fullName) {
      name = user.fullName;
    } else if (user.name) {
      name = user.name;
    } else if (user.firstName || user.lastName) {
      name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    } else if (user.email) {
      name = user.email;
    }
    
    // Add role context
    let roleContext = '';
    if (user.programRole === 'club_management') {
      roleContext = ` (Club ${user.clubRole || 'Management'})`;
    } else if (user.programRole && user.programRole !== 'player') {
      roleContext = ` (${user.programRole})`;
    } else if (user.programStatus === 'enrolled') {
      roleContext = ' (Program Member)';
    } else if (user.programRole === 'club_member') {
      roleContext = ' (Club Member)';
    }
    
    return `${name}${roleContext}`;
  };

  // Initialize form with assignment data if editing
  useEffect(() => {
    if (assignmentToEdit) {
      console.log('AssignEquipmentModal - Populating form with assignment data:', assignmentToEdit);
      
      // Handle both object and string IDs for playerId
      const playerId = assignmentToEdit.playerId?._id || assignmentToEdit.playerId || '';
      const equipmentId = assignmentToEdit.equipmentId?._id || assignmentToEdit.equipmentId || '';
      
      console.log('AssignEquipmentModal - Extracted playerId:', playerId);
      console.log('AssignEquipmentModal - Extracted equipmentId:', equipmentId);
      
      setSelectedPlayer(playerId);
      setSelectedEquipment(equipmentId);
      setNotes(assignmentToEdit.notes || '');
      setDueDate(assignmentToEdit.dueDate ? assignmentToEdit.dueDate.split('T')[0] : '');
      setQuantity(assignmentToEdit.quantity || 1);
      setCondition(assignmentToEdit.condition || 'good');
      
      console.log('AssignEquipmentModal - Form populated with values:', {
        playerId,
        equipmentId,
        notes: assignmentToEdit.notes || '',
        dueDate: assignmentToEdit.dueDate ? assignmentToEdit.dueDate.split('T')[0] : '',
        quantity: assignmentToEdit.quantity || 1,
        condition: assignmentToEdit.condition || 'good'
      });
    } else if (!preSelectedEquipment) {
      console.log('AssignEquipmentModal - No assignmentToEdit provided, resetting form for new assignment');
      // Reset form for new assignment (only if no pre-selected equipment)
      setSelectedPlayer('');
      setSelectedEquipment('');
      setNotes('');
      setDueDate('');
      setQuantity(1);
      setCondition('good');
    }
  }, [assignmentToEdit, preSelectedEquipment]);

  // Cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      console.log('AssignEquipmentModal - Modal closed, resetting form');
      resetForm();
    }
  }, [isOpen]);

  const handleAssign = async (e) => {
    e.preventDefault();
    setAssigning(true);
    setError(null);
    setSuccess(false);

    try {
      const assignmentData = {
        programId, // Add programId to associate assignment with the program
        playerId: selectedPlayer,
        equipmentId: selectedEquipment,
        notes,
        dueDate,
        quantity: parseInt(quantity),
        condition,
      };

      console.log('Creating assignment with data:', assignmentData);
      console.log('Program ID being sent:', programId);

      let response;
      if (assignmentToEdit) {
        // Update existing assignment
        response = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/equipment-assignments/${assignmentToEdit._id}`,
          assignmentData
        );
      } else {
        // Create new assignment
        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/equipment-assignments`,
          assignmentData
        );
      }

      // Success handling
      console.log('Equipment assignment successful:', response.data);
      
      // Show success message
      setSuccess(true);
      
      // Call the callback to update parent component
      if (onAssignmentUpdated) {
        onAssignmentUpdated(response.data);
      }
      
      // Close the modal after a brief delay to show success message
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 1500);
      
    } catch (error) {
      console.error('Error assigning equipment:', error);
      setError(
        error.response?.data?.message || 'Failed to assign equipment'
      );
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FaUsers className={styles.modalTitleIcon} />
            <h3>
              {assignmentToEdit
                ? 'Edit Equipment Assignment'
                : 'Assign Equipment to Player'}
            </h3>
          </div>
          <div className={styles.headerControls}>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close Modal"
            >
              <FaTimes />
            </button>
          </div>
        </div>
        
        <div className={styles.modalBody}>
          {error && <div className={styles.errorMessage}>
            <FaTimes className={styles.errorIcon} />
            {error}
          </div>}
          {success && (
            <div className={styles.successMessage}>
              <FaCheckCircle className={styles.successIcon} />
              Equipment assignment successful!
            </div>
          )}
          
          {/* Show pre-selected equipment info */}
          {preSelectedEquipment && (
            <div className={styles.preSelectedInfo}>
              <FaBox className={styles.preSelectedIcon} />
              <div className={styles.preSelectedText}>
                <strong>Assigning:</strong> {preSelectedEquipment.equipmentName}
              </div>
            </div>
          )}
          
          <form onSubmit={handleAssign} className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="player" className={styles.formLabel}>
                  <FaUsers className={styles.formLabelIcon} />
                  Select Player *
                </label>
                {loadingPlayers ? (
                  <p>Loading players...</p>
                ) : (
                  <select
                    id="player"
                    value={selectedPlayer}
                    onChange={(e) => setSelectedPlayer(e.target.value)}
                    required
                    className={styles.formSelect}
                  >
                    <option value="">-- Select Player --</option>
                    {players.map((player) => (
                      <option key={player._id} value={player._id}>
                        {formatUserDisplay(player)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="equipment" className={styles.formLabel}>
                  <FaBox className={styles.formLabelIcon} />
                  Select Equipment *
                </label>
                {loadingEquipments ? (
                  <p>Loading equipments...</p>
                ) : (
                  <select
                    id="equipment"
                    value={selectedEquipment}
                    onChange={(e) => setSelectedEquipment(e.target.value)}
                    required
                    className={styles.formSelect}
                    disabled={!!preSelectedEquipment} // Disable if pre-selected
                  >
                    <option value="">-- Select Equipment --</option>
                    {equipments.map((equipment) => (
                      <option key={equipment._id} value={equipment._id}>
                        {equipment.equipmentName}
                      </option>
                    ))}
                  </select>
                )}
                {preSelectedEquipment && (
                  <small style={{fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: '4px'}}>
                    Equipment pre-selected from card
                  </small>
                )}
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="quantity" className={styles.formLabel}>
                  <FaHashtag className={styles.formLabelIcon} />
                  Quantity
                </label>
                <input
                  type="number"
                  id="quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min="1"
                  required
                  className={styles.formInput}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="condition" className={styles.formLabel}>
                  <FaStar className={styles.formLabelIcon} />
                  Condition
                </label>
                <select
                  id="condition"
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  required
                  className={styles.formSelect}
                >
                  <option value="new">New</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                </select>
              </div>
            </div>
            
            <div className={styles.formGroupFull}>
              <label htmlFor="dueDate" className={styles.formLabel}>
                <FaCalendarAlt className={styles.formLabelIcon} />
                Return By Date
              </label>
              <input
                type="date"
                id="dueDate"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={styles.formInput}
              />
            </div>
            
            <div className={styles.formGroupFull}>
              <label htmlFor="notes" className={styles.formLabel}>
                <FaStickyNote className={styles.formLabelIcon} />
                Special Instructions
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special instructions for this assignment..."
                className={styles.formTextarea}
              ></textarea>
            </div>
          </form>
        </div>
        
        <div className={styles.buttonContainer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onClose}
            disabled={assigning}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`${styles.submitButton} ${success ? styles.success : ''}`}
            onClick={handleAssign}
            disabled={assigning || success}
          >
            {success
              ? 'Success!'
              : assigning
              ? assignmentToEdit
                ? 'Saving...'
                : 'Assigning...'
              : assignmentToEdit
              ? 'Save Changes'
              : 'Assign Equipment'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

AssignEquipmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  programId: PropTypes.string.isRequired,
  assignmentToEdit: PropTypes.object,
  onAssignmentUpdated: PropTypes.func,
  preSelectedEquipment: PropTypes.object,
};

export default AssignEquipmentModal;
